/** @jsxRuntime classic */
/** @jsx jsx */
import { jsx } from '@emotion/core';
import React, { ReactElement, Fragment } from 'react';
import styled, { CSSObject } from '@emotion/styled';
import { getPatternModal } from '../../../style';
import Avatar from '../../../atoms/Avatar';
import Headline from '../../common/Headline';
import Button from '../../../atoms/buttons/Button';
import ButtonSet from '../../blocks/ButtonSet';
import { trackUnlockMessage } from '../../../actions/profileTrackAction';
import useMixPanel from '../../../hooks/useMixPanel';
import { EVENT_NAMES } from '../../../constants/mixpanel';
import { useNavigate } from 'react-router-dom';
import { unlockOutsideMessage } from '../../../actions/mailActions';
import { useDispatch } from 'react-redux';
import useMandatoryWall from '../../../hooks/useMandatoryWall';
import ModalWithPattern from 'components/common/modals/ModalWithPattern';
import { IsMobileViewPort } from 'utils/helpers';
import { mobileContentStyle, mobileOverlayStyle } from 'components/common/CommonStyle';
import OfferModalFooter from './OfferModalFooter';
import breakpoints from 'theme/material/breakpoints';
import borders from 'theme/borders';

const Form = styled('form')({
  display: 'flex',
  flexDirection: 'column',
  height: '100%',
});

const HeaderDiv = styled('div')({
  textAlign: 'center',

  [breakpoints.down(639.05)]: {
    marginTop: 10,
  },
});

const AvatarTitleDiv = styled('div')({
  display: 'flex',
  flexDirection: 'column',
  flexWrap: 'wrap',
  justifyContent: 'center',
  alignItems: 'center',
  margin: '0 auto',
  gap: 20,
});

const TitleDiv = styled('span')(
  (): CSSObject => ({
    color: getPatternModal().titleColor,
    fontSize: 36,
    fontWeight: 800,
    lineHeight: '40px',
    letterSpacing: -0.3,
    textTransform: 'uppercase',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    [breakpoints.down(639.05)]: {
      fontSize: 30,
    },
  })
);

const Content = styled('div')({
  display: 'flex',
  flexDirection: 'column',
  flexGrow: 1,
  justifyContent: 'space-between',
});

const ButtonDiv = styled('div')({
  margin: '20px 0',
});

interface OfferAcceptUnlockProps {
  isModalOpen: boolean;
  offerAmount: any;
  avatarSrc: any;
  username: any;
  buttonID: any;
  successOpen: any;
  creditCost: any;
  hashId: any;
  currentUserIsGenerous: any;
  closeModal: any;
  currentCredit: any;
  threadUid?: any;
  customContentStyle?: any;
}

const OfferAcceptUnlock = ({
  isModalOpen,
  offerAmount,
  avatarSrc,
  username,
  buttonID,
  successOpen,
  creditCost,
  hashId,
  currentUserIsGenerous,
  closeModal,
  currentCredit,
  customContentStyle,
}: OfferAcceptUnlockProps): ReactElement => {
  const dispatch = useDispatch();
  const { mixpanelData, resetMixpanelData, trackPackagesPageVisited } = useMixPanel();
  const { showMandatoryWall } = useMandatoryWall();

  const navigate = useNavigate();

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    successOpen(false);

    trackUnlockMessage();

    if (creditCost > currentCredit) {
      trackPackagesPageVisited({ Source: 'Unlock Attempt' });
      navigate('/packages');
      return;
    }

    showMandatoryWall(
      () => {
        dispatch(
          unlockOutsideMessage(
            hashId,
            mixpanelData[EVENT_NAMES.CONVERSATION_UNLOCKED].triggerToBuy,
            () => {
              resetMixpanelData(EVENT_NAMES.CONVERSATION_UNLOCKED);
            },
            currentUserIsGenerous,
            navigate
          )
        );
      },
      () => undefined,
      'Unlock Offer'
    );
  };

  const getHeadline = () => {
    // Your first message has been unlocked.
    const subText = currentUserIsGenerous ? (
      <p style={{ margin: 0, marginTop: 10 }}>
        Unlock messaging for <strong>{creditCost} Credits</strong> and start planning your date!
      </p>
    ) : (
      <p style={{ margin: 0, marginTop: 10 }}>Send {username} a note and start planning your date!</p>
    );

    return (
      <div>
        <span>
          You&lsquo;ve accepted <strong>{username}</strong>&lsquo;s offer of {offerAmount}.{' '}
        </span>
        <br />
        {subText}
      </div>
    );
  };

  return (
    <ModalWithPattern
      isModalOpen={isModalOpen}
      dataTestID="offer-accept-unlock"
      closeBtnClassName="offer-accept-unlock-modal-close"
      closeBtnDataTestId="offer-accept-unlock-modal-close"
      modalContentLabel="Offer Accept Unlock"
      onCloseClick={closeModal}
      closeRequest={closeModal}
      customOverlayStyle={IsMobileViewPort ? mobileOverlayStyle : {}}
      customContentStyle={IsMobileViewPort ? { ...mobileContentStyle, ...customContentStyle } : customContentStyle}
      withoutBgPattern={false}
      hideCloseBtn={false}
      closeOnOverlayClick={true}
      enableTouchMove={true}
    >
      <Fragment>
        <Form>
          <HeaderDiv>
            <AvatarTitleDiv>
              <Avatar
                data-test-id="offer-modal-avatar"
                src={avatarSrc}
                width={'100px'}
                customStyle={{
                  backgroundColor: '#ccc',
                  border: `1px solid ${borders.light.primary}`,
                }}
              />
              <TitleDiv>Offer Accepted!</TitleDiv>
            </AvatarTitleDiv>
            <Headline
              style={{
                color: getPatternModal().headerColor,
                marginTop: 20,
                fontSize: 16,
                lineHeight: '21px',
                letterSpacing: '-0.3px',
              }}
              data-test-id="profile-offer-headline"
              size="SM"
            >
              {getHeadline()}
            </Headline>
          </HeaderDiv>
          <Content>
            <ButtonDiv>
              <ButtonSet align="center">
                <Button id={buttonID} buttonType="error" onClick={handleSubmit} type="submit">
                  {currentUserIsGenerous ? 'Unlock Messaging' : 'Go to Conversation'}
                </Button>
              </ButtonSet>
            </ButtonDiv>
          </Content>
        </Form>
      </Fragment>
      <OfferModalFooter />
    </ModalWithPattern>
  );
};

export default OfferAcceptUnlock;
